import { Alert, AlertAction, AlertOverviewEntry, getDate, SummaryProfile } from '@watcherr/resources';
import { formatDate } from '@watcherr/ui';
import { differenceInSeconds, endOfDay, startOfDay } from 'date-fns';
import { t } from '~i18n';
import { downloadAsBlob, formatDateTimeExport } from '~utils/export';

function determineLocationType(location: any) {
  if (!location) return null;
  if (location.floorName) return `INDOOR`;

  if (location.accuracy) return `OUTDOOR`;
}
function displayOutdoorLocation(location: any) {
  if (location.accuracy) return `${location.x} - ${location.y} ${location.accuracy}`;
}
function displayFloorName(location: any) {
  if (location.floorName) return location.floorName;
}
function displayRoomNumber(location: any) {
  return location.roomNumber ?? location.zoneName;
}

export async function exportAlerts(start: Date, end: Date) {
  const alertUpdateEvents = await Alert.getAlertsHistoryV2({
    start_date: startOfDay(start).toISOString(),
    end_date: endOfDay(end).toISOString(),
  });
  const header = [
    'Uuid',
    'Priority',
    'Event-type',
    'Alert-status',
    'Event-action',
    'Resident',
    'Device-name',
    'Device-MAC',
    'Created',
    'Location-type',
    'Floor-name',
    'Room-number',
    'Coordinates',
    'HCP-provider',
    'Rejected-time',
    'Accepted-time',
    'Resolved-time',
    'Cancelled-time',
    'Seconds-until-accepted',
    'Seconds-until-resolved',
    'Seconds-until-cancelled',
    'Total-seconds',
    'Proximity-control',
    'Resolve-reason',
  ].join(', ');
  const groupedByAlert: object = Object.groupBy(alertUpdateEvents, (aue) => aue.uuid);
  const alertUpdateEventsWithSummaryRow: AlertOverviewEntry[] = [];
  Object.keys(groupedByAlert).forEach((uuid) => {
    const summaryRow = groupedByAlert[uuid].reduce((acc, cur) => {
      // If only 1 event, no summaryRow
      if (groupedByAlert[uuid].length == 1) {
        return {};
      }
      // The summaryRow should contain the data of the latest event
      // groupedByAlert is sorted by created desc, so first reduce should always be the latest event
      if (!Object.keys(acc).length) {
        acc = cur;
      }
      if (cur.eventAction == AlertAction.Accept) {
        acc.acceptedTime = cur.acceptedTime;
      }
      if (cur.eventAction == AlertAction.Reject) {
        acc.rejectedTime = cur.rejectedTime;
      }
      if (cur.cancelledTime) {
        acc.cancelledTime = cur.cancelledTime;
      }
      return acc;
    }, {});

    if (Object.keys(summaryRow).length) {
      alertUpdateEventsWithSummaryRow.push(summaryRow);
    }
    alertUpdateEventsWithSummaryRow.push(...groupedByAlert[uuid]);
  });
  const rows = alertUpdateEventsWithSummaryRow.map((a) => {
    const secondsUntilAccepted = a.acceptedTime
      ? differenceInSeconds(getDate(a.acceptedTime), getDate(a.created))
      : null;
    const secondsUntilResolved = a.resolvedTime
      ? differenceInSeconds(getDate(a.resolvedTime), getDate(a.created))
      : null;
    const secondsUntilCancelled = a.cancelledTime
      ? differenceInSeconds(getDate(a.cancelledTime), getDate(a.created))
      : null;
    const endDate = a.resolvedTime ?? a.cancelledTime;
    const totalSeconds = endDate ? differenceInSeconds(getDate(endDate), getDate(a.created)) : null;
    const eventHcp: SummaryProfile | null = a.resolvedBy ?? a.acceptedBy ?? a.rejectedBy ?? null;
    const locationType = determineLocationType(a.location ?? a.device?.installationLocation);
    const row = [
      a.uuid,
      a.priority,
      t(`${a.eventType}`),
      a.status,
      a.eventAction,
      a.resident ? `${a.resident.lastName} ${a.resident.firstName}` : null,
      a.device ? a.device.description : null,
      a.device?.macAddress ? a.device?.macAddress : null,
      formatDateTimeExport(a.created),
      locationType,
      locationType == 'INDOOR' ? displayFloorName(a.location ?? a.device?.installationLocation) : null,
      locationType == 'INDOOR' ? displayRoomNumber(a.location ?? a.device?.installationLocation) : null,
      locationType == 'OUTDOOR' ? displayOutdoorLocation(a.location) : null,
      eventHcp ? `${eventHcp.firstName} ${eventHcp.lastName}` : null,
      a.rejectedTime ? formatDateTimeExport(a.rejectedTime) : null,
      a.acceptedTime ? formatDateTimeExport(a.acceptedTime) : null,
      a.resolvedTime ? formatDateTimeExport(a.resolvedTime) : null,
      a.cancelledTime ? formatDateTimeExport(a.cancelledTime) : null,
      secondsUntilAccepted,
      secondsUntilResolved,
      secondsUntilCancelled,
      totalSeconds,
      a.cancelledTime
        ? 'CANCELLED'
        : !a.proximityResolveAction && a.eventAction == AlertAction.Resolve
          ? 'DASHBOARD'
          : a.proximityResolveAction,
      a.resolvedReason,
    ];
    return row.join(', ');
  });
  const data = [header, ...rows].join('\n');
  downloadAsBlob(`alerts-export-${formatDate(start)}-${formatDate(end)}`, data);
}
